// Exercise catalog queries. The catalog is seeded server-side with the
// 63-exercise legacy library, so consumers can treat the response as the full
// list and filter client-side.
import type { Exercise, MuscleGroup } from '@onflow/shared';
import { useQuery } from '@tanstack/react-query';

import { api } from '../lib/api.js';

export interface ExerciseFilters {
  q?: string;
  muscle?: MuscleGroup;
  equipment?: string;
}

function buildQuery(filters: ExerciseFilters): string {
  const params = new URLSearchParams();
  if (filters.q) params.set('q', filters.q);
  if (filters.muscle) params.set('muscle', filters.muscle);
  if (filters.equipment) params.set('equipment', filters.equipment);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// Fetches the full catalog once and caches forever (catalog is read-only data).
export function useExercises() {
  return useQuery({
    queryKey: ['exercises', 'catalog'],
    queryFn: () => api<Exercise[]>('/exercises', { auth: false }),
    staleTime: Infinity,
  });
}

// Used when the caller wants the API to do the filtering (large catalogs).
// The current 63-exercise list is tiny, so we usually filter client-side
// instead of calling this.
export function useFilteredExercises(filters: ExerciseFilters) {
  return useQuery({
    queryKey: ['exercises', 'filtered', filters],
    queryFn: () => api<Exercise[]>(`/exercises${buildQuery(filters)}`, { auth: false }),
    staleTime: 60_000,
  });
}
